import { AppSwitcher } from '@teamworksdev/vuetify';
import { computed } from 'vue';
import { useGmApi } from './useGmApi';
import { handleError } from '@/utils/errorHandling';
import { useUserStore } from '@/stores/userStore';
import { normalizeTwrn, encodeTwrn } from '@/utils/helpers';

export function useAppSwitcher() {
  const GmApi = useGmApi();
  const userStore = useUserStore();

  const appSwitcher = computed(() => new AppSwitcher(
    AppSwitcher.slugs.GM,
    () => GmApi.getOutgoingAxleToken()
      .then(({ token }) => token)
      .catch((e) => {
        handleError(e);
        return '';
      }),
    userStore.axleProfile?.appTargetUrls ?? [],
    userStore.axleProfile?.loginAccess ?? {},
    !!userStore.impersonator,
  ));

  /**
   * Deep links to an athlete's Hub profile via their Axle TWRN in a new browser tab.
   * An optional `section` (e.g. `attributes?openedCategories=`, `forms`) targets
   * a sub-page of the profile.
   * See HUB-11661 / the "Deep Linking to Hub Profiles via TWRN" platform doc.
   */
  async function redirectToHubProfile(twrn: string, section?: string): Promise<void> {
    const encodedTwrn = encodeTwrn(normalizeTwrn(twrn), { urlEncode: true });
    const redirectPath = section
      ? `/profiles/${encodedTwrn}/${section}`
      : `/profiles/${encodedTwrn}`;

    const error = await appSwitcher.value.switch(
      AppSwitcher.slugs.HUB,
      { redirect: redirectPath },
      { openInNewTab: true },
    );
    if (error) throw error;
  }

  return {
    appSwitcher,
    redirectToHubProfile,
  };
}
